import { onBeforeUnmount, shallowRef, toValue, watch, type MaybeRefOrGetter } from "vue";
import H from "@signup/base/Helpers";
import { AxiosRequestError } from "@signup/ajax/AxiosAjaxClient";
import { usePreloader } from "@signup/composables/usePreloader";
import TrustSiteApi from "@dashboard/ajax/TrustSiteApi";
import TrustSiteApiService from "@dashboard/ajax/TrustSiteApiService";
import type { BrandConfig } from "@dashboard/brands/brandConfig";
import { useBannerStore } from "@store/useBannerStore";
import { useDashboardStore } from "@store/useDashboardStore";
import { createRequestGate } from "@dashboard/utils/requestGate";

/**
 * Owns initial load, guest polling and brand-switch reloads for one dashboard screen.
 * Does not derive incidents or events; those live on the store and useDashboardView.
 * @param brandConfig - Production pages pass a fixed config. A reactive brand reloads
 * aliases and banners so the demo can switch brands without remounting.
 */
export function useDashboardSession(brandConfig: MaybeRefOrGetter<BrandConfig>) {
  const api = new TrustSiteApi();
  const dashboardStore = useDashboardStore();
  const bannerStore = useBannerStore();
  const { showPreloader, hidePreloader } = usePreloader();
  const isLoading = shallowRef(false);
  const error = shallowRef<string | null>(null);
  // Snapshot for this instance's polling policy, not reactive authentication state.
  const logged = TrustSiteApiService.isLoggedIn();
  const autoUpdateInterval = 60_000;
  const autoUpdateTimerId = shallowRef<ReturnType<typeof setInterval> | null>(null);
  // Store gates protect data; this gate protects the complete screen loading cycle.
  const loadCycles = createRequestGate();
  let pendingUpdate: Promise<void> | null = null;
  let unmounted = false;
  let preloaderShown = false;
  let ownedReportRequestId: number | null = null;
  let ownedBannerRequestId: number | null = null;

  function releasePreloader(): void {
    if (preloaderShown) {
      hidePreloader();
      preloaderShown = false;
    }
  }

  function abandonOwnedRequests(): void {
    if (ownedReportRequestId !== null) {
      dashboardStore.abandonReportRequest(ownedReportRequestId);
      ownedReportRequestId = null;
    }

    if (ownedBannerRequestId !== null) {
      bannerStore.abandonBannerRequest(ownedBannerRequestId);
      ownedBannerRequestId = null;
    }
  }

  function applyBrand(config: BrandConfig): void {
    dashboardStore.setRegionAliases(config.regionAliases);
    if (!config.loadBanners) {
      bannerStore.setBannerGroups([]);
    }
  }

  async function loadDashboard(): Promise<void> {
    const cycleId = loadCycles.start();
    const isCurrent = () => !unmounted && loadCycles.isCurrent(cycleId);
    const config = toValue(brandConfig);
    isLoading.value = true;
    error.value = null;

    try {
      const reportRequestId = dashboardStore.startReportRequest();
      ownedReportRequestId = reportRequestId;
      if (unmounted) {
        abandonOwnedRequests();
        return;
      }

      applyBrand(config);
      const reportApplied = await dashboardStore.fetchReport(api, reportRequestId);
      if (!reportApplied || !isCurrent()) {
        return;
      }

      if (config.loadBanners) {
        const bannerRequestId = bannerStore.startBannerRequest();
        ownedBannerRequestId = bannerRequestId;
        if (unmounted) {
          abandonOwnedRequests();
          return;
        }

        const bannersApplied = await bannerStore.fetchBanners(api, bannerRequestId);
        if (!bannersApplied || !isCurrent()) {
          return;
        }
      }

      // The original dashboard released its loader only after report and banners succeeded.
      releasePreloader();
    } catch (err) {
      if (!isCurrent()) {
        return;
      }

      error.value = err instanceof Error ? err.message : String(err);
      // These failures never reached the original dashboard catch because its transport
      // left the Promise pending. Preserve the page while allowing the next guest poll.
      if (err instanceof AxiosRequestError && (
        err.kind === "network" || err.kind === "timeout" || err.responseDataMissing
      )) {
        return;
      }

      window.location.replace("/");
      H.error(`Can't update status dashboard! ${err}`);
    } finally {
      if (isCurrent()) {
        isLoading.value = false;
      }
    }
  }

  function updateDashboard(): Promise<void> {
    if (unmounted) {
      return Promise.resolve();
    }

    if (!pendingUpdate) {
      const run = loadDashboard().finally(() => {
        if (pendingUpdate === run) {
          pendingUpdate = null;
        }
      });
      pendingUpdate = run;
    }

    return pendingUpdate;
  }

  function restartDashboard(): Promise<void> {
    if (unmounted) {
      return Promise.resolve();
    }

    loadCycles.invalidate();
    abandonOwnedRequests();
    pendingUpdate = null;
    return updateDashboard();
  }

  onBeforeUnmount(() => {
    unmounted = true;
    loadCycles.invalidate();
    abandonOwnedRequests();
    isLoading.value = false;
    if (autoUpdateTimerId.value !== null) {
      clearInterval(autoUpdateTimerId.value);
      autoUpdateTimerId.value = null;
    }
    releasePreloader();
  });

  showPreloader();
  preloaderShown = true;
  void updateDashboard();

  watch(
    () => toValue(brandConfig).brand,
    (nextBrand, previousBrand) => {
      if (previousBrand === undefined || nextBrand === previousBrand) {
        return;
      }

      void restartDashboard();
    },
  );

  if (!logged) {
    autoUpdateTimerId.value = setInterval(
      () => { void updateDashboard(); },
      autoUpdateInterval,
    );
  }

  return {
    isLoading,
    error,
    logged,
    updateDashboard,
  };
}
